import React, { FC } from 'react';

import { NativeResources } from '@ariksa/inventory-core';
import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { map } from 'lodash';
import { useSelector } from 'react-redux';
import { VulnerabilityPackageSummary } from 'services/Vulnerability/types';

import {
  dateLabel,
  DetailsIdentifier,
  getIcon,
  renderRiskContextIcon,
  renderTime,
} from 'components/DataDisplay';
import { renderVulnerabilities } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import {
  renderIsAMIPublic,
  renderResourceState,
  renderScanOnPush,
  renderVulnerabilityCloudIDField,
  renderVulnerabilityImageField,
  renderVulnerabilityOSField,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/utils';
import { VulnerabilityActions } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/VulnerabilityActions';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

export interface Props {
  summary: VulnerabilityPackageSummary;
  details?: Record<string, any>;
}

export const VulnerabilityResourceDetails: FC<Props> = props => {
  const { summary, details } = props;
  const {
    vulnerabilitySummary,
    resourceInsight,
    riskContext,
    resourceType,
  } = useSelector(selectActiveResource);
  const { data } = vulnerabilitySummary;
  const { data: insight } = resourceInsight;
  const {
    renderStackedCloudAccountWithCloud,
    accountMapping,
  } = useCloudAccountId();

  const renderDeployedIn = () => (
    <HStack spacing={1}>
      {renderStackedCloudAccountWithCloud(insight?.account, true)}
      <Box>/</Box>
      <Box>{accountMapping?.[insight?.account]?.cloud_account_id}</Box>
      {insight?.region && (
        <>
          <Box>/</Box>
          <Box>{insight?.region}</Box>
        </>
      )}
      {(insight?.vpc || insight?.vpc_id) && (
        <>
          <Box>/</Box>
          <Box>{insight?.vpc ?? insight?.vpc_id}</Box>
        </>
      )}
    </HStack>
  );

  //const renderCloud

  return (
    <Stack spacing={5}>
      <VulnerabilityActions />
      {resourceType !== NativeResources.ElasticContainerRegistry && (
        <HStack spacing={10}>
          <Stack spacing={5} w="50%">
            {renderVulnerabilityCloudIDField(insight?.resource_id)}
            <DetailsIdentifier
              label={
                resourceType === NativeResources.AmazonMachineImage
                  ? 'Account'
                  : 'Deployed In'
              }
              value={renderDeployedIn()}
            />
            {resourceType !== NativeResources.AmazonMachineImage && (
              <DetailsIdentifier
                label="State"
                value={
                  insight?.state ? renderResourceState(insight?.state) : '-'
                }
              />
            )}
            <DetailsIdentifier
              label="Vulnerabilities"
              value={renderVulnerabilities({ value: summary })}
            />
          </Stack>
          <Stack spacing={5} w="50%">
            {resourceType === NativeResources.AmazonMachineImage ? (
              <DetailsIdentifier label="Used By" /> /*value={'Virtual Machines'}*/
            ) : (
              renderVulnerabilityImageField(insight?.image)
            )}
            {renderVulnerabilityOSField(data?.name, data?.version)}

            {resourceType !== NativeResources.AmazonMachineImage && (
              <>
                <DetailsIdentifier
                  label="Kubernetes"
                  //value={renderVulnerabilities(insight?.vu)}
                />
                <DetailsIdentifier
                  label="Risk Context"
                  align="center"
                  value={
                    <HStack spacing={0}>
                      {map(riskContext.data, (o, index) => (
                        <Box key={index + '-risk-context'}>
                          {renderRiskContextIcon(o)}
                        </Box>
                      ))}
                    </HStack>
                  }
                />
              </>
            )}
            {resourceType === NativeResources.AmazonMachineImage && (
              <DetailsIdentifier
                label="Public"
                value={renderIsAMIPublic(insight?.public)}
              />
            )}
          </Stack>
        </HStack>
      )}
      {resourceType === NativeResources.ElasticContainerRegistry && (
        <Stack spacing={5}>
          <HStack spacing={10}>
            <Stack spacing={5} w="50%">
              <DetailsIdentifier
                label="Registry"
                value={
                  <HStack>
                    <Box boxSize={4}>
                      <Center>{getIcon(insight?.provider)}</Center>
                    </Box>
                    <Box>{insight?.repository_uri}</Box>
                  </HStack>
                }
              />
              <DetailsIdentifier
                label="Name"
                value={details?.metadata?.image_name}
              />
              <DetailsIdentifier
                label="Vulnerabilities"
                value={renderVulnerabilities({ value: summary })}
              />
            </Stack>
            <Stack spacing={5} w="50%">
              <DetailsIdentifier
                label="Used by"
                value={
                  !!details?.containers_count
                    ? details?.containers_count +
                      ' Container' +
                      (details?.containers_count === 1 ? '' : 's')
                    : 'None'
                }
              />
              {/*<DetailsIdentifier
                label="Scan On Push"
                value={renderScanOnPush(
                  details?.config?.imageScanningConfiguration?.scanOnPush,
                )}
              />*/}
              <DetailsIdentifier
                label="Created At"
                value={dateLabel(details?.created_at)}
              />
              {/*<DetailsIdentifier
                label="Image Tag"
                value={details?.config?.imageTagMutability}
              />*/}
              <DetailsIdentifier
                label="Last Scanned"
                value={dateLabel(details?.last_scanned_at)}
              />
            </Stack>
          </HStack>
          <DetailsIdentifier
            label="Cloud ID"
            value={
              [
                insight?.name,
                details?.image_digest,
                details?.metadata?.image_name ||
                  details?.image_tags?.join(', '),
              ]
                .filter(Boolean)
                .join(' / ') || '-'
            }
          />
          <DetailsIdentifier label="Labels" />
        </Stack>
      )}
    </Stack>
  );
};
